import React, { useEffect, useState } from "react"
import clsx from "clsx"
import { Grid, Typography, IconButton, Link as LinkMUI } from "@material-ui/core"
import { Close as CloseIcon, EmojiObjectsOutlined as TipsIcon, ChevronRight as ChevronRightIcon } from "@material-ui/icons"

import { WABAChannelChatFreeEntryPointWindowStatus } from "@/@integrations/WABA/protocols/wabaChannelChatFreeEntryPointWindow"
import { useMetaFreeEntryPointWindow } from "@/@integrations/WABA/hooks/useMetaFreeEntryPointWindow"
import { openMessageWasteTipsModal } from "@/@integrations/WABA/components/Chat/MessageWasteTipsModal"
import { MetaFreeEntryPointWindowIcon } from "@/assets/icons"
import { MINUTE_IN_MS } from "@/utils/time"
import { WABALinks } from "@/utils/link"

import useStyles from "@/@integrations/WABA/components/Chat/MetaFreeEntryPointWindowBar/styles"

type MetaFreeEntryPointWindowBarProps = {
	status: WABAChannelChatFreeEntryPointWindowStatus
	scheduledPauseAt?: Date
}

type BannerRange = "standard" | "warning"

const MetaFreeEntryPointWindowBar: React.FC<MetaFreeEntryPointWindowBarProps> = (props) => {
	const {
		status,
		scheduledPauseAt
	} = props

	const classes = useStyles()

	const [, setCountdownTick] = useState<number>(0)
	const [dismissedRange, setDismissedRange] = useState<BannerRange | null>(null)
	const [isInactiveBannerDismissed, setIsInactiveBannerDismissed] = useState<boolean>(false)

	const {
		canShowExpiringWarning,
		canShowPauseDate,
		formattedRemainingTime
	} = useMetaFreeEntryPointWindow({ status, scheduledPauseAt })

	const currentRange: BannerRange = canShowExpiringWarning ? "warning" : "standard"
	const isBannerDismissed = dismissedRange === currentRange

	useEffect(() => {
		if (!canShowPauseDate) {
			return
		}

		const countdownInterval = setInterval(() => {
			setCountdownTick(tick => tick + 1)
		}, MINUTE_IN_MS)

		return () => {
			clearInterval(countdownInterval)
		}
	}, [canShowPauseDate])

	const handleDismissBanner = () => {
		setDismissedRange(currentRange)
	}

	const handleDismissInactiveBanner = () => {
		setIsInactiveBannerDismissed(true)
	}

	if (!canShowPauseDate) {
		if (isInactiveBannerDismissed) {
			return null
		}

		return (
			<Grid
				container
				className={clsx(classes.container, classes.containerInactive)}
			>
				<Grid item xs={12}>
					<Grid
						container
						wrap="wrap"
						alignItems="center"
					>
						<Grid item>
							<TipsIcon className={clsx(classes.icon, classes.iconInactive)} />
						</Grid>
						<Grid item>
							<Typography className={clsx(classes.statusMessage, classes.statusMessageInactive)}>
								Sem custo reduzido nesta conversa. <strong>Evite mensagens picadas</strong> para não multiplicar o custo por mensagem.
							</Typography>
						</Grid>
						<Grid item>
							<LinkMUI
								onClick={openMessageWasteTipsModal}
								className={clsx(classes.link, classes.linkInactive)}
							>
								Ver dicas
								<ChevronRightIcon fontSize="small" style={{ verticalAlign: "middle" }} />
							</LinkMUI>
						</Grid>
					</Grid>
				</Grid>
				<IconButton
					size="small"
					onClick={handleDismissInactiveBanner}
					className={clsx(classes.closeButton, classes.closeButtonInactive)}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Grid>
		)
	}

	if (isBannerDismissed) {
		return null
	}

	return (
		<Grid
			container
			className={clsx(classes.container, {
				[classes.containerWarning]: canShowExpiringWarning
			})}
		>
			<Grid item xs={12}>
				<Grid
					container
					wrap="wrap"
					alignItems="center"
				>
					<Grid item>
						<MetaFreeEntryPointWindowIcon className={clsx(classes.icon, {
							[classes.iconWarning]: canShowExpiringWarning
						})} />
					</Grid>
					<Grid item>
						<Typography
							className={clsx(classes.statusMessage, {
								[classes.statusMessageWarning]: canShowExpiringWarning
							})}
						>
							{`Modo Meta: custo reduzido - ${formattedRemainingTime}`}
						</Typography>
					</Grid>
					<Grid item>
						<LinkMUI
							href={WABALinks.metaFreeEntryPointWindowHelpArticle}
							target="_blank"
							rel="noopener noreferrer"
							className={clsx(classes.link, {
								[classes.linkWarning]: canShowExpiringWarning
							})}
						>
							Saiba mais
						</LinkMUI>
					</Grid>
				</Grid>
			</Grid>
			<IconButton
				size="small"
				onClick={handleDismissBanner}
				className={clsx(classes.closeButton, {
					[classes.closeButtonWarning]: canShowExpiringWarning
				})}
			>
				<CloseIcon fontSize="small" />
			</IconButton>
		</Grid>
	)
}

export default MetaFreeEntryPointWindowBar
